import React, { useCallback, useEffect, useState, useMemo } from 'react'
import { Checkbox, List, Tag } from 'antd'
import PropTypes from 'prop-types'
import moment from 'moment'
import { createUseStyles, useTheme } from 'react-jss'
import cn from 'classnames'

import { useAsync } from '../../hooks'
import api from '../../api'

const REPORTS = {
  alerts: 'Alerts',
  officeChecks: 'Office checks',
  receivedTransmissions: 'Received transmissions',
}

const getLink = entry => {
  if (entry.type === 'alerts') return `/alerts/${entry.id}`
  if (entry.type === 'receivedTransmissions') return `/reports/${entry.id}`
  if (entry.type === 'officeChecks') return `/office-checks/${entry.id}`
  return ''
}

const ReportSidebar = ({ report }) => {
  const {
    patient: { id: patientId, practiceId },
  } = report
  const [visible, setVisible] = useState([])

  const { brandSuccess, blue, brandDangerMid, brandPrimary } = useTheme()
  const useStyles = createUseStyles({
    entries: {
      fontSize: '.9em',
      maxHeight: '400px',
      overflowY: 'scroll',
      '&::after': {
        content: '""',
        background: `linear-gradient(to bottom, transparent, white 20px)`,
        height: '20px',
        width: '100%',
        position: 'absolute',
        bottom: 0,
      },
    },
    marginRight: {
      marginRight: '1em',
      display: 'inline-block',
    },
    green: {
      color: 'green',
    },
    orange: {
      color: 'orange',
    },
    miniBlock: {
      display: 'inline-block',
      width: '10px',
      height: '10px',
      marginRight: '.5em',
      borderRadius: '4px',
    },
    alertColor: {
      backgroundColor: `${brandDangerMid} !important`,
    },
    officeCheckColor: {
      backgroundColor: `${brandSuccess} !important`,
    },
    receivedTransmissionsColor: {
      backgroundColor: `${blue} !important`,
    },
    entryDescription: {
      color: 'black',
      fontSize: '.9em',
    },
    signed: {
      marginLeft: '.5em',
    },
    filters: {
      marginBottom: '1em',
      paddingBottom: '.5em',
      borderBottom: '1px solid #e8e8e8',
    },
    title: {
      color: brandPrimary,
      fontSize: '1.2em',
    },
    bottom: {
      marginBottom: '1em',
    },
  })
  const classes = useStyles()

  const getReports = useCallback(
    async () => api.patients.getReports(patientId),
    [patientId],
  )
  const getAlerts = useCallback(async () => api.patients.getAlerts(patientId), [
    patientId,
  ])
  const getOfficeChecks = useCallback(
    async () => api.patients.getOfficeChecks(practiceId, patientId),
    [patientId, practiceId],
  )
  const receivedTransmissions = useAsync(getReports)
  const alerts = useAsync(getAlerts)
  const officeChecks = useAsync(getOfficeChecks)

  const [diseases, setDiseases] = useState([])

  useEffect(() => {
    api.diseases.getDiseases(patientId).then(data => setDiseases(data))
  }, [patientId])

  const allEntries = useMemo(() => {
    return [
      ...(receivedTransmissions.value?.body || []).map(entry => ({
        ...entry,
        type: 'receivedTransmissions',
      })),
      ...(alerts.value?.body || []).map(entry => ({
        ...entry,
        type: 'alerts',
      })),
      ...(officeChecks.value?.body || []).map(entry => ({
        ...entry,
        type: 'officeChecks',
      })),
    ]
      .map(entry => {
        return {
          ...entry,
          date: entry?.cycleEndedAt || entry?.reportDate || entry?.checkDate,
        }
      })
      .sort((a, b) => {
        if (a.date > b.date) return -1
        if (a.date < b.date) return 1
        return 0
      })
  }, [alerts.value, officeChecks.value, receivedTransmissions.value])

  const visibleEntries = useMemo(() => {
    return allEntries.filter(entry => visible.includes(entry.type))
  }, [allEntries, visible])

  const getClass = useCallback(
    entry => {
      if (entry.type === 'alerts') return classes.alertColor
      if (entry.type === 'receivedTransmissions')
        return classes.receivedTransmissionsColor
      if (entry.type === 'officeChecks') return classes.officeCheckColor
      return 'red'
    },
    [classes],
  )

  const options = useMemo(
    () =>
      Object.keys(REPORTS)
        .filter(key => allEntries.filter(e => e.type === key).length > 0)
        .map(key => ({
          value: key,
          label: (
            <span>
              <span
                className={cn(classes.miniBlock, getClass({ type: key }))}
              />
              {`${REPORTS[key]} (${
                allEntries.filter(e => e.type === key).length
              })`}
            </span>
          ),
        })),
    [allEntries, classes.miniBlock, getClass],
  )

  useEffect(
    () =>
      setVisible(
        allEntries.reduce(
          (sum, entry) =>
            !sum.includes(entry.type) ? [...sum, entry.type] : sum,
          [],
        ),
      ),
    [allEntries, setVisible],
  )

  return (
    <div>
      <div className={classes.bottom}>
        <h3 className={classes.title}>Diseases</h3>
        {diseases.map(disease => (
          <>
            {disease.name}
            <br />
          </>
        ))}
      </div>
      <h3 className={classes.title}>Reports, alerts & office checks</h3>
      <div className={classes.filters}>
        <Checkbox.Group
          options={options}
          onChange={setVisible}
          value={visible}
        />
      </div>
      <div className={classes.entries}>
        <List
          size="small"
          dataSource={visibleEntries}
          renderItem={entry => (
            <List.Item>
              <List.Item.Meta
                title={
                  <a
                    href={getLink(entry)}
                    rel="noopener noreferrer"
                    target="_blank"
                  >
                    <span className={cn(classes.miniBlock, getClass(entry))} />
                    {moment(entry.date).format('MMM Do YYYY')}
                    {'signedAt' in entry && (
                      <span className={classes.signed}>
                        {entry.signedAt ? (
                          <Tag color="green">Signed</Tag>
                        ) : (
                          <Tag color="orange">Not signed</Tag>
                        )}
                      </span>
                    )}
                  </a>
                }
              />
            </List.Item>
          )}
        />
      </div>
    </div>
  )
}

ReportSidebar.propTypes = {
  report: PropTypes.shape({
    atrialFibrilationFlutterDisease: PropTypes.bool,
    anticoagulatedDisease: PropTypes.bool,
    patient: PropTypes.shape({
      id: PropTypes.number,
      practiceId: PropTypes,
    }).isRequired,
  }),
}

ReportSidebar.defaultProps = {
  report: {
    atrialFibrilationFlutterDisease: false,
    anticoagulatedDisease: false,
  },
}

export default ReportSidebar
